import swal from 'sweetalert';

(() => {
  angular.module('app').component('hospitalPatientCardComponent', {
    templateUrl: require('./patientCard.component.html'),
    controller: HospitalPatientCardController,
    controllerAs: 'vm',
    bindings: {
      employee: '<',
      panelLocationId: '<',
      getEmployees: '&',
      disciplines: '<',
    },
  });

  HospitalPatientCardController.$inject = [
    '$state',
    '$uibModal',
    'abp.services.app.hospitalPanelServiceRequest',
    'abp.services.app.inpatientEmployee',
    'Hms.GuaranteeLetters.GuaranteeLetterStatus',
    'Hms.ServiceRequests.ServiceRequestStatus',
    'Hms.Services.ServiceType',
    'Hms.ServiceRequests.ServiceRequestType',
    'moment',
    'expiredGuaranteeLetterFlow',
  ];

  function HospitalPatientCardController(
    $state,
    $uibModal,
    hospitalPanelServiceRequestSvc,
    inpatientEmployeeSvc,
    enumGuaranteeLetterStatus,
    enumServiceRequestStatus,
    enumServiceType,
    enumServiceRequestType,
    moment,
    expiredGuaranteeLetterFlow
  ) {
    const vm = this;

    vm.loading = 0;
    vm.currencyCode = abp.setting.get('Hms.General.CurrencyCode');
    vm.panelNumber = abp.setting.get('Hms.General.SpecialistSupportContactNumber');
    vm.pendingApprovalGlNarrative = App.localize(
      'PendingApprovalGuaranteeLetterNarrative',
      vm.panelNumber
    );
    vm.hasSpecialistWorkflowEnhancement = abp.setting.getBoolean(
      'Hms.Feature.SpecialistWorkflowEnhancement'
    );

    vm.enums = {};
    vm.enums.serviceRequestStatus = enumServiceRequestStatus;
    vm.enums.guaranteeLetterStatus = enumGuaranteeLetterStatus;
    vm.enums.serviceType = enumServiceType;
    vm.enums.serviceRequestType = enumServiceRequestType;

    // Set the initial value of employeeNoAccessMessage to null.
    vm.employeeNoAccessMessage = null;

    // Check if the employee has access to hospitalization or maternity.
    if (!vm.employee.hasHospitalizationAccess && !vm.employee.hasMaternityAccess) {
      // Set the employeeNoAccessMessage when there is no access to both services.
      vm.employeeNoAccessMessage = App.localize('PanelCannotApplyGLDueToOrgPolicy');
    } else if (!vm.employee.hasHospitalizationAccess && vm.employee.hospitalizationBenefitGroupId) {
      // Set the employeeNoAccessMessage for the case of no hospitalization access.
      vm.employeeNoAccessMessage = App.localize(
        'PanelCannotApplyXGLDueToOrgPolicy',
        App.localize(vm.enums.serviceType.Hospitalization.name)
      );
    } else if (!vm.employee.hasMaternityAccess && vm.employee.maternityBenefitGroupId) {
      // Set the employeeNoAccessMessage for the case of no maternity access.
      vm.employeeNoAccessMessage = App.localize(
        'PanelCannotApplyXGLDueToOrgPolicy',
        App.localize(vm.enums.serviceType.Maternity.name)
      );
    }

    vm.checkInEmployee = checkInEmployee;
    vm.showCancelModal = showCancelModal;
    vm.showExpiredGuaranteeLetter = showExpiredGuaranteeLetter;
    vm.getDateString = getDateString;
    vm.getUserBgColor = getUserBgColor;
    vm.getUserInitial = getUserInitial;

    vm.$onInit = init;

    function init() {
      getTickets();
    }

    /* Employee icon */

    function getUserBgColor() {
      return `user_bgcolor_${((vm.employee.id || 0) % 8) + 1}`;
    }

    function getUserInitial() {
      const fullName = (vm.employee.name || ' ').split(' ');
      let initial = '';
      for (let i = 0; i < fullName.length; i += 1) {
        if (fullName[i].length) {
          initial += fullName[i][0];
        }
      }
      if (initial && initial.length > 3) initial = `${initial[0]}`;
      return initial;
    }

    /* End of employee icon */

    /* Functions */

    function checkInEmployee() {
      // An expired guarantee letter cannot be checked in or rescheduled — offer cancel-and-reapply
      // instead of sending the hospital into a check-in the server will refuse (HM-11144).

      if (expiredGuaranteeLetterFlow.isExpired({
        status: vm.employee.guaranteeLetter.glStatus,
        checkInExpiryTime: vm.employee.guaranteeLetter.checkInExpiryTime,
      })) {
        showExpiredGuaranteeLetter();
        return;
      }

      const today = moment().format('YYYY-MM-DD');
      const scheduledTime = moment(vm.employee.guaranteeLetter.scheduledAdmissionTime).format(
        'YYYY-MM-DD'
      );
      if (!scheduledTime) return;

      if (scheduledTime === today) {
        swal(
          {
            title: App.localize('ConfirmAdmission'),
            text: App.localize('ConfirmAdmissionNarrative'),
            confirmButtonColor: '#1ab394',
            confirmButtonText: App.localize('Yes'),
            cancelButtonText: App.localize('No'),
            showCancelButton: true,
            closeOnConfirm: false,
          },
          (isConfirm) => {
            if (isConfirm) {
              checkIn('');
              vm.employee.isGLSubmittedOrApproved = false;
              vm.employee.checkedInTime = vm.employee.guaranteeLetter.scheduledAdmissionTime;
            }
            swal.close();
          }
        );
      } else {
        $uibModal
          .open({
            templateUrl: require('../admission/admission.modal.html'),
            controller: 'clinic.views.hospital.patient.admission as vm',
            backdrop: 'static',
            resolve: {
              scheduleDate() {
                return vm.employee.guaranteeLetter.scheduledAdmissionTime;
              },
              expiryDate() {
                return vm.employee.guaranteeLetter.expiryTime;
              },
              letterNumber() {
                return vm.employee.guaranteeLetter.glNumber;
              },
            },
          })
          .result.then((data) => {
            if (data.isCheckIn) checkIn(data);
          });
      }
    }

    function checkIn(data) {
      vm.loading += 1;
      hospitalPanelServiceRequestSvc
        .checkIn({
          guaranteeLetterNumber: vm.employee.guaranteeLetter.glNumber,
          remarks: data.reason,
          actualAdmissionDate: data.actualAdmissionDate,
          newAttachments: data.newAttachments,
        })
        .success((resp) => {
          if (resp.printUrl !== '') {
            abp.notify.info(App.localize('PatientXCheckedIn', vm.employee.name));
            window.open(resp.printUrl);
            goToTimeline();
          } else {
            if (resp.isEarlyCheckIn) abp.notify.info(App.localize('EarlyCheckInRequestSubmitted'));
            else abp.notify.info(App.localize('LateCheckInRequestSubmitted'));
            vm.refreshFunction();
          }
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function goToTimeline() {
      $state.go('clinic.patientAdmissionDetails', {
        letterNumber: vm.employee.guaranteeLetter.glNumber,
      });
    }

    function showExpiredGuaranteeLetter() {
      const guaranteeLetter = vm.employee.guaranteeLetter;

      expiredGuaranteeLetterFlow.prompt({
        letterNumber: guaranteeLetter.glNumber,
        scheduledAdmissionTime: guaranteeLetter.scheduledAdmissionTime,
        reapplyParams: {
          employeeId: vm.employee.id,
          panelLocationId: vm.panelLocationId,
        },
        onCancelled: () => vm.getEmployees(),
      });
    }

    function showCancelModal(letterNumber, letterRequestType, letterStatus) {
      $uibModal
        .open({
          templateUrl: require('../../common/cancellationModal/cancel.modal.html'),
          controller: 'clinic.views.hospital.common.cancelModal as vm',
          backdrop: 'static',
          size: 'lg',
          resolve: {
            letterRequestType() {
              return letterRequestType;
            },
            letterStatus() {
              return letterStatus;
            },
          },
        })
        .result.then((cancellationReason) => {
          if (cancellationReason) {
            const input = {
              letterNumber,
              cancellationReason,
            };

            vm.loading += 1;

            hospitalPanelServiceRequestSvc
              .cancelGuaranteeLetterServiceRequest(input)
              .success(() => {
                abp.notify.success('Cancelled');
              })
              .finally(() => {
                vm.loading -= 1;
                vm.getEmployees();
              });
          }
        });
    }

    function getTickets() {
      vm.loading += 1;
      inpatientEmployeeSvc
        .getPreviousVisits({
          id: vm.employee.id,
        })
        .success((data) => {
          vm.patientTickets = data;

          _.forEach(vm.patientTickets, (item) => {
            item.disciplineId = _.transform(item.disciplineId, (result, disciplineId) => {
              _(vm.disciplines).forEach((specialistIdName) => {
                if (disciplineId === specialistIdName.value) {
                  return result.push(specialistIdName.name);
                }
              });
            }).join(', ');
          });

          _.forEach(vm.patientTickets, (item) => {
            if (item.finalDiagnoses.length > 0) {
              item.finalDiagnoses = _.reduce(
                item.finalDiagnoses,
                (first, second) => `${first}, ${second}`
              );
            }
          });
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function getDateString(date) {
      return App.getDateString(moment.utc(date));
    }

    /* End of Functions */
  }
})();
